import { useReducer, useRef, RefObject } from "react";

// State interface for the messages component
export interface MessagesState {
  rowsData: any[];
  messageContent: any;
  selectedKeys: Set<any> | undefined;
  selectedDataRow: any;
  navigationIndex: number;
  showLoader: boolean;
  showLoaderDetail: boolean;
  showDetail: boolean;
  stopLoadData: boolean;
  totalMessageListRec: number;
  showNewMessageDialog: boolean;
  showListName: string;
  touchStart: any; // Added for touch start event handling
  touchEnd: any; // Added for touch end event handling
  messageNodeId: number;
  // Reply-related state
  replyType?: string;
  replyMessageObj?: any;
  replyEmailAttachments?: any[];
}

// Action types with proper payload typing
export type MessagesAction =
  | { type: "SET_ROWS_DATA"; payload: any[] }
  | { type: "APPEND_ROWS_DATA"; payload: any[] }
  | { type: "SET_MESSAGE_CONTENT"; payload: any }
  | { type: "SET_SELECTED_KEYS"; payload: Set<any> | undefined }
  | { type: "SET_SELECTED_DATA_ROW"; payload: any }
  | { type: "SET_NAVIGATION_INDEX"; payload: number }
  | { type: "SET_SHOW_LOADER"; payload: boolean }
  | { type: "SET_SHOW_LOADER_DETAIL"; payload: boolean }
  | { type: "SET_SHOW_DETAIL"; payload: boolean }
  | { type: "SET_STOP_LOAD_DATA"; payload: boolean }
  | { type: "SET_TOTAL_MESSAGE_LIST_REC"; payload: number }
  | { type: "SET_SHOW_NEW_MESSAGE_DIALOG"; payload: boolean }
  | { type: "SET_SHOW_LIST_NAME"; payload: string }
  | { type: "RESET_STATE"; payload?: Partial<MessagesState> }
  | { type: "RESET_MESSAGE_LIST"; payload?: { filterText?: string } }
  | { type: "SET_TOUCH_START"; payload: string }
  | { type: "SET_TOUCH_END"; payload: string }
  | { type: "SET_MESSAGE_NODE_ID"; payload: number }
  // Reply-related actions
  | { type: "SET_REPLY_TYPE"; payload: string }
  | { type: "SET_REPLY_MESSAGE_OBJ"; payload: any }
  | { type: "SET_REPLY_EMAIL_ATTACHMENTS"; payload: any[] };

// Initial state
const initialState: MessagesState = {
  rowsData: [],
  messageContent: "",
  selectedKeys: undefined,
  selectedDataRow: {},
  navigationIndex: 0,
  showLoader: true,
  showLoaderDetail: true,
  showDetail: false,
  stopLoadData: false,
  totalMessageListRec: 0,
  showNewMessageDialog: false,
  showListName: "",
  touchStart: "",
  touchEnd: "",
  messageNodeId: 0,
  // Reply-related state
  replyType: undefined,
  replyMessageObj: undefined,
  replyEmailAttachments: undefined,
};

// Reducer with immutable updates and better performance
function messagesReducer(
  state: MessagesState,
  action: MessagesAction
): MessagesState {
  switch (action.type) {
    case "SET_ROWS_DATA":
      return { ...state, rowsData: action.payload };

    case "APPEND_ROWS_DATA":
      return { ...state, rowsData: [...state.rowsData, ...action.payload] };

    case "SET_MESSAGE_CONTENT":
      return { ...state, messageContent: action.payload };

    case "SET_SELECTED_KEYS":
      return { ...state, selectedKeys: action.payload };

    case "SET_SELECTED_DATA_ROW":
      return { ...state, selectedDataRow: action.payload };

    case "SET_NAVIGATION_INDEX":
      return { ...state, navigationIndex: action.payload };

    case "SET_SHOW_LOADER":
      return { ...state, showLoader: action.payload };

    case "SET_SHOW_LOADER_DETAIL":
      return { ...state, showLoaderDetail: action.payload };

    case "SET_SHOW_DETAIL":
      return { ...state, showDetail: action.payload };

    case "SET_STOP_LOAD_DATA":
      return { ...state, stopLoadData: action.payload };

    case "SET_TOTAL_MESSAGE_LIST_REC":
      return { ...state, totalMessageListRec: action.payload };

    case "SET_SHOW_NEW_MESSAGE_DIALOG":
      return {
        ...state,
        showNewMessageDialog: action.payload,
      };

    case "SET_SHOW_LIST_NAME":
      return { ...state, showListName: action.payload };

    case "SET_TOUCH_START":
      return { ...state, touchStart: action.payload };

    case "SET_TOUCH_END":
      return { ...state, touchEnd: action.payload };

    case "SET_MESSAGE_NODE_ID":
      return { ...state, messageNodeId: action.payload };

    // Reply-related cases
    case "SET_REPLY_TYPE":
      return { ...state, replyType: action.payload };

    case "SET_REPLY_MESSAGE_OBJ":
      return { ...state, replyMessageObj: action.payload };

    case "SET_REPLY_EMAIL_ATTACHMENTS":
      return { ...state, replyEmailAttachments: action.payload };

    case "RESET_STATE":
      return { ...initialState, ...action.payload };

    case "RESET_MESSAGE_LIST":
      return {
        ...state,
        stopLoadData: false,
        showLoader: true,
        showLoaderDetail: true,
        selectedKeys: new Set(),
        selectedDataRow: {},
        messageContent: "",
        navigationIndex: 0,
        totalMessageListRec: 0,
        rowsData: [],
        messageNodeId: 0,
      };

    default:
      return state;
  }
}

// Refs interface for better organization
export interface MessagesRefs {
  componentRef: RefObject<HTMLDivElement | null>;
  searchedTextRef: RefObject<string>;
  filterParamRef: RefObject<{
    createdFrom: string;
    createdTo: string;
    sender: string;
    subject: string;
    newOnly: boolean;
    starred: boolean;
  }>;
  offSetLimitRef: RefObject<number>;
  abortControllerRef: RefObject<AbortController | null>;
  listMessageListRef: RefObject<any>;
}

// Custom hook for messages state management
export function useMessagesState() {
  const [state, dispatch] = useReducer(messagesReducer, initialState);

  // Organized refs
  const refs: MessagesRefs = {
    componentRef: useRef<HTMLDivElement>(null),
    searchedTextRef: useRef<string>(""),
    filterParamRef: useRef({
      createdFrom: "",
      createdTo: "",
      sender: "",
      subject: "",
      newOnly: false,
      starred: false,
    }),
    offSetLimitRef: useRef<number>(0),
    abortControllerRef: useRef<AbortController | null>(null),
    listMessageListRef: useRef<any>(null),
  };

  // Action creators for better readability
  const actions = {
    setRowsData: (payload: any[]) =>
      dispatch({ type: "SET_ROWS_DATA", payload }),
    appendRowsData: (payload: any[]) =>
      dispatch({ type: "APPEND_ROWS_DATA", payload }),
    setMessageContent: (payload: any) =>
      dispatch({ type: "SET_MESSAGE_CONTENT", payload }),
    setSelectedKeys: (payload: Set<any> | undefined) =>
      dispatch({ type: "SET_SELECTED_KEYS", payload }),
    setSelectedDataRow: (payload: any) =>
      dispatch({ type: "SET_SELECTED_DATA_ROW", payload }),
    setNavigationIndex: (payload: number) =>
      dispatch({ type: "SET_NAVIGATION_INDEX", payload }),
    setShowLoader: (payload: boolean) =>
      dispatch({ type: "SET_SHOW_LOADER", payload }),
    setShowLoaderDetail: (payload: boolean) =>
      dispatch({ type: "SET_SHOW_LOADER_DETAIL", payload }),
    setShowDetail: (payload: boolean) =>
      dispatch({ type: "SET_SHOW_DETAIL", payload }),
    setStopLoadData: (payload: boolean) =>
      dispatch({ type: "SET_STOP_LOAD_DATA", payload }),
    setTotalMessageListRec: (payload: number) =>
      dispatch({ type: "SET_TOTAL_MESSAGE_LIST_REC", payload }),
    setShowNewMessageDialog: (payload: boolean) =>
      dispatch({ type: "SET_SHOW_NEW_MESSAGE_DIALOG", payload }),
    setShowListName: (payload: string) =>
      dispatch({ type: "SET_SHOW_LIST_NAME", payload }),
    setTouchStart: (payload: any) =>
      dispatch({ type: "SET_TOUCH_START", payload }),
    setTouchEnd: (payload: any) => dispatch({ type: "SET_TOUCH_END", payload }),
    setMessageNodeId: (payload: number) =>
      dispatch({ type: "SET_MESSAGE_NODE_ID", payload }),
    // Reply-related actions
    setReplyType: (payload: string) =>
      dispatch({ type: "SET_REPLY_TYPE", payload }),
    setReplyMessageObj: (payload: any) =>
      dispatch({ type: "SET_REPLY_MESSAGE_OBJ", payload }),
    setReplyEmailAttachments: (payload: any[]) =>
      dispatch({ type: "SET_REPLY_EMAIL_ATTACHMENTS", payload }),
    resetState: (payload?: Partial<MessagesState>) =>
      dispatch({ type: "RESET_STATE", payload }),
    resetMessageList: (payload?: { filterText?: string }) =>
      dispatch({ type: "RESET_MESSAGE_LIST", payload }),
  };

  return {
    state,
    dispatch,
    actions,
    refs,
  };
}
